import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState, useCallback } from "react";
import {
  ProFormSelect,
  ProFormText,
  ProForm,
  ProFormDateRangePicker,
  ProFormDateTimeRangePicker,
  ProFormDatePicker,
  ProFormRadio,
} from "@ant-design/pro-components";
import { Form, Button, Checkbox } from "antd";
import { useTranslation } from "react-i18next";
import { Params, FilterParams } from "@/facilities/model/user";
import NetworkSelect from "@/components/NetworkSelect";
import clsx from "clsx";
import dayjs from "@/utils/dayjsConfig";
import { parseQueryString, findMenuByPath, parmsFromat } from "@/utils/toolt";
import { useLocation, useNavigate } from "react-router-dom";
import { pickBy, intersection, isEqual, compact } from "lodash-es";
import { FAULT_TABLESTATUS, FORMATDATE, MONTH_FORMAT } from "@/enums/pageEnum";
import CustomModalSelect from "../CustomModalSelect";
import GroupTexts from "../GroupTexts";
import { useAppSelector } from "@/store";

const excludeParmsKey = [FAULT_TABLESTATUS];

const FilterComponet = ({
  fieldsProp,
  submit,
  reset,
  className,
  submitNode,
  children,
  ref,
  isAddParamsToUrl = true,
  layout = "inline",
}: {
  fieldsProp?: FilterParams[];
  initValues?: Params;
  className?: string;
  layout?: "horizontal" | "vertical" | "inline";
  submitNode?: React.ReactNode;
  children?: React.ReactNode;
  ref?: any;
  isAddParamsToUrl?: boolean;
  reset?: () => void;
  submit?: (formData: Record<string, any>) => void;
}) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const location = useLocation();
  const { pathname, hash } = location;
  const [form] = Form.useForm();
  const menus = useAppSelector((state) => state.app.menus);
  const [isInitStaut, setIsInitStaut] = useState<boolean>(true);
  const keyDown = useCallback(
    (e: any) => {
      const parms = pickBy(form.getFieldsValue());
      if (e.keyCode === 13 && Object.keys(parms).length) {
        form.submit();
      }
    },
    [form]
  );

  const getCurrentMenu = useCallback(() => {
    const pathPasm = findMenuByPath(menus?.routes || [], pathname);
    return pathPasm ? pathPasm.defaultParams : null;
  }, [pathname, menus]);

  const setButtonStatus = useCallback(
    (param: any) => {
      const paramKeys = Object.keys(param);
      const defparam = getCurrentMenu();
      if (
        !paramKeys.length ||
        (paramKeys.length === 1 &&
          intersection(excludeParmsKey, paramKeys).length) ||
        (defparam && isEqual(param, defparam))
      ) {
        setIsInitStaut(true);
        return;
      }
      setIsInitStaut(false);
    },
    [getCurrentMenu]
  );
  useEffect(() => {
    const copyParam = parseQueryString();
    const param = { ...copyParam };
    const paramKeys = Object.keys(param);
    if (param.types) {
      param.types = compact(param.types.split(","));
    }
    if (
      param.month &&
      fieldsProp?.find((item) => item.field === "month")?.component !== "select"
    ) {
      param.month = dayjs(param.month, MONTH_FORMAT);
    }
    if (!paramKeys.length) {
      form?.resetFields();
    } else {
      form?.setFieldsValue(pickBy(param));
    }
    setButtonStatus(copyParam);
  }, [location, form, setButtonStatus]);
  useEffect(() => {
    window.addEventListener("keydown", keyDown, false);
    return () => {
      window.removeEventListener("keydown", keyDown, false);
    };
  }, []);
  const getFields = (item: FilterParams) => {
    const {
      field,
      component,
      label,
      options,
      apiUrl,
      fieldProps,
      dataSource,
      defaultValue,
      dataKey,
      isShowPagination,
      isUniqBy,
    } = item;
    switch (component) {
      case "text":
        return (
          <ProFormText
            name={field}
            key={field}
            label={`${label}: `}
            fieldProps={{
              className: "w-[190px]",
              allowClear: false,
              ...fieldProps,
            }}
            placeholder={label}
          />
        );
      case "select":
        return (
          <ProFormSelect
            options={options}
            key={field}
            name={field}
            fieldProps={{ className: "w-[130px]", ...fieldProps }}
            label={label ? `${label}: ` : ""}
          />
        );
      case "apiSelect":
        return (
          <NetworkSelect
            key={field}
            field={field}
            apiUrl={apiUrl}
            fieldProps={fieldProps}
            dataKey={dataKey}
            isUniqBy={isUniqBy}
            isShowPagination={isShowPagination}
            label={`${label}: `}
          />
        );
      case "multipleInput":
        return (
          <ProFormSelect
            name={field}
            key={field}
            label={`${label}: `}
            className="multipleEmail"
            placeholder={label}
            fieldProps={{
              mode: "tags",
              style: { width: 130 },
              popupClassName: "emailClassName",
            }}
          />
        );
      case "groupTexts":
        return <GroupTexts key={field} fields={item} />;
      case "groupTags":
        return (
          <ProForm.Item noStyle key={field} shouldUpdate>
            {() => {
              return (
                <div
                  className={`${fieldProps?.divClass} pb-4 reportByClass relative`}
                >
                  <ProFormRadio.Group
                    options={options}
                    label={`${label}: `}
                    name={field}
                    fieldProps={{
                      optionType: "button",
                      buttonStyle: "solid",
                      defaultValue,
                    }}
                  />
                  <span className="ml-2 inline-block absolute left-[570px] top-1">
                    <Checkbox onChange={() => {}}>Completed DLP</Checkbox>
                  </span>
                </div>
              );
            }}
          </ProForm.Item>
        );
      case "dateRange":
        return (
          <ProFormDateRangePicker
            key={field}
            name={field}
            fieldProps={{
              style: { width: 220 },
              maxDate: dayjs(new Date()),
              inputReadOnly: true,
            }}
            placeholder={["Start date", "End date"]}
            label={`${label}: `}
          />
        );
      case "timeRange":
        return (
          <ProFormDateTimeRangePicker
            key={field}
            name={field}
            fieldProps={{
              style: { width: 360 },
              maxDate: dayjs(new Date()),
              format: FORMATDATE,
              inputReadOnly: true,
            }}
            placeholder={["Start date", "End date"]}
            label={`${label}: `}
          />
        );
      case "monthDate":
        return (
          <div key={field} className={fieldProps?.divClass}>
            <ProFormDatePicker
              name={field}
              fieldProps={{
                picker: "month",
                ...fieldProps,
                inputReadOnly: true,
              }}
              label={`${label}: `}
            />
          </div>
        );
      case "cSel":
        return (
          <ProForm.Item
            name={field}
            className={`${fieldProps?.divClass} pb-4`}
            key={field}
            label={`${label}: `}
          >
            <CustomModalSelect dataSource={dataSource} label={label} />
          </ProForm.Item>
        );
      default:
        return;
    }
  };
  return (
    <ProForm
      layout={layout}
      form={form}
      className={clsx(
        "bg-[#fff] dark:bg-[#141414] py-5 pl-4 customFilterCentext",
        className
      )}
      submitter={{
        render: (props) => {
          return [
            <Button
              type="primary"
              key="submit"
              className="pr-4 pl-4"
              onClick={() => props.form?.submit?.()}
            >
              {submitNode || (
                <SearchOutlined
                  className="text-[#fff]"
                  style={{ fontSize: 16 }}
                />
              )}
            </Button>,
            <Button
              key="rest"
              color="primary"
              variant="outlined"
              disabled={isInitStaut}
              onClick={() => {
                setIsInitStaut(true);
                navigation(parmsFromat(getCurrentMenu(), pathname), {
                  replace: true,
                });
                props.form?.resetFields();
                reset?.();
              }}
            >
              {t("reset")}
            </Button>,
          ];
        },
      }}
      onValuesChange={(_, values) => {
        setButtonStatus(values);
      }}
      onFinish={(values) => {
        setIsInitStaut(false);
        if (isAddParamsToUrl) {
          const path = new URLSearchParams(pickBy(values)).toString();
          navigation(
            `${pathname}${hash.split("?")[0]}${
              path ? `?${encodeURIComponent(path)}` : ""
            }`
          );
        }
        submit?.(values);
      }}
      formRef={ref}
    >
      {fieldsProp?.map((item) => getFields(item))}
      {children}
    </ProForm>
  );
};

export default FilterComponet;
